// src/components/modules/ExportDataDialog.jsx
// Diálogo profissional de exportação de telemetria: resumo executivo +
// pré-visualização (JSON com destaque de sintaxe / tabela CSV) antes de
// baixar, com opção de copiar pra área de transferência. PDF continua sendo
// gerado sob demanda (é uma captura visual do dashboard, não tem preview
// textual que faça sentido mostrar aqui).
import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Chip,
    Stack,
    Divider,
    alpha,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { saveBlobAs, supportsFilePicker } from '../../utils/saveFile';

const FORMATS = [
    { key: 'json', label: 'JSON', icon: Lucide.Braces, mime: 'application/json', ext: 'json' },
    { key: 'csv', label: 'CSV', icon: Lucide.FileText, mime: 'text/csv;charset=utf-8', ext: 'csv' },
    { key: 'pdf', label: 'PDF', icon: Lucide.FileType, mime: null, ext: 'pdf' },
];

function flattenSnapshot(obj) {
    const rows = [];
    const walk = (o, prefix) => {
        if (o == null) return;
        for (const [k, v] of Object.entries(o)) {
            const key = prefix ? `${prefix}.${k}` : k;
            if (v != null && typeof v === 'object' && !Array.isArray(v)) {
                walk(v, key);
            } else {
                rows.push([key, v ?? '']);
            }
        }
    };
    walk(obj, '');
    return rows;
}

function toCsv(rows) {
    return 'Parâmetro,Valor\n' + rows.map(([k, v]) => `"${k}","${v}"`).join('\n');
}

// Destaque de sintaxe leve pro JSON, sem depender de biblioteca externa.
function highlightJson(json) {
    const esc = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return esc.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
            let cls = 'json-number';
            if (/^"/.test(match)) cls = /:$/.test(match) ? 'json-key' : 'json-string';
            else if (/true|false/.test(match)) cls = 'json-boolean';
            else if (/null/.test(match)) cls = 'json-null';
            return `<span class="${cls}">${match}</span>`;
        }
    );
}

function formatBytes(str) {
    const bytes = new Blob([str]).size;
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
}

function fileTimestamp() {
    return new Date().toISOString().slice(0, 19).replace(/:/g, '-');
}

const STATUS_LABEL = {
    busy: 'Ensaio em andamento',
    locked: 'Sistema bloqueado',
    available: 'Sistema disponível',
};
const STATUS_COLOR = { busy: 'info', locked: 'warning', available: 'success' };

function SummaryChip({ label, value, color }) {
    if (value == null) return null;
    return (
        <Box
            sx={{
                px: 1.25,
                py: 0.6,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 92,
            }}
        >
            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} fontFamily="monospace" sx={{ color }}>
                {value}
            </Typography>
        </Box>
    );
}

export default function ExportDataDialog({
    open,
    onClose,
    snapshot,
    serialNumber,
    onDownloadPDF,
    pdfGenerating,
    onSaved,
}) {
    const theme = useTheme();
    const [format, setFormat] = useState('json');
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);

    const rows = useMemo(() => (snapshot ? flattenSnapshot(snapshot) : []), [snapshot]);
    const jsonText = useMemo(
        () => (snapshot ? JSON.stringify(snapshot, null, 2) : ''),
        [snapshot]
    );
    const csvText = useMemo(() => toCsv(rows), [rows]);
    const activeFormat = FORMATS.find((f) => f.key === format);
    const activeText = format === 'json' ? jsonText : format === 'csv' ? csvText : '';
    const fileBaseName = `telemetria_${serialNumber}_${fileTimestamp()}`;
    // Navegador permite escolher a pasta de destino? (muda o rotulo do botao)
    const picker = supportsFilePicker();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(activeText);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // Clipboard indisponível (ex.: contexto não seguro) — sem ação,
            // o usuário ainda pode selecionar o texto manualmente na prévia.
        }
    };

    const handleDownload = async () => {
        if (format === 'pdf') {
            onDownloadPDF?.();
            return;
        }
        const filename = `${fileBaseName}.${activeFormat.ext}`;
        const blob = new Blob([activeText], { type: activeFormat.mime });
        setSaving(true);
        try {
            // Pergunta ONDE salvar (Chrome/Edge); nos demais navegadores cai no
            // download tradicional. Se o usuario cancelar, nao salva nada.
            const res = await saveBlobAs(blob, filename, {
                description: `Telemetria (${activeFormat.label})`,
                mime: activeFormat.mime,
                ext: activeFormat.ext,
            });
            if (res.canceled) return;
            onSaved?.(filename);
        } catch (e) {
            onSaved?.(null, e);
        } finally {
            setSaving(false);
        }
    };

    const diagnosticsCount = snapshot?.diagnostics?.length ?? 0;
    const colorimetryEntries = [
        { key: 'turbidimetry', label: 'Turbidimetria' },
        { key: 'nephelometry', label: 'Nefelometria' },
        { key: 'fluorescence', label: 'Fluorescência' },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    pb: 1.5,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Exportar Dados
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                    >
                        {serialNumber}
                        {snapshot?.timestamp &&
                            ` · ${new Date(snapshot.timestamp).toLocaleString('pt-BR')}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Resumo executivo — visão clara e objetiva antes dos dados brutos */}
            <Box sx={{ px: 3, py: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: colorimetryEntries.some((c) => snapshot?.colorimetry?.[c.key]) ? 1.5 : 0 }}>
                    {snapshot?.status && (
                        <Chip
                            size="small"
                            label={STATUS_LABEL[snapshot.status] || snapshot.status.toUpperCase()}
                            color={STATUS_COLOR[snapshot.status] || 'default'}
                            sx={{ fontWeight: 700 }}
                        />
                    )}
                    <SummaryChip
                        label="Amostra"
                        value={
                            snapshot?.temperatures?.sample != null
                                ? `${snapshot.temperatures.sample.toFixed(1)}°C`
                                : null
                        }
                    />
                    <SummaryChip
                        label="Potência"
                        value={
                            snapshot?.powerSupply?.watts != null
                                ? `${snapshot.powerSupply.watts.toFixed(1)} W`
                                : null
                        }
                    />
                    {diagnosticsCount > 0 && (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${diagnosticsCount} alerta${diagnosticsCount > 1 ? 's' : ''}`}
                        />
                    )}
                </Stack>

                {colorimetryEntries.some((c) => snapshot?.colorimetry?.[c.key]) && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {colorimetryEntries.map(({ key, label }) => {
                            const c = snapshot?.colorimetry?.[key];
                            if (!c) return null;
                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.6,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: c.color || 'action.disabledBackground',
                                            flexShrink: 0,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {label}
                                    </Typography>
                                    <Typography variant="caption" fontWeight={700} fontFamily="monospace">
                                        Y={c.Y?.toFixed(3) ?? '—'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            <Divider />

            <Box sx={{ px: 3, pt: 1.5 }}>
                <Tabs
                    value={format}
                    onChange={(_, v) => setFormat(v)}
                    variant="fullWidth"
                >
                    {FORMATS.map((f) => (
                        <Tab
                            key={f.key}
                            value={f.key}
                            icon={<f.icon size={18} />}
                            iconPosition="start"
                            label={f.label}
                            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
                        />
                    ))}
                </Tabs>
            </Box>

            <DialogContent dividers sx={{ p: 0 }}>
                {format === 'pdf' ? (
                    <Box sx={{ p: 5, textAlign: 'center' }}>
                        <Lucide.FileType size={56} color={theme.palette.text.disabled} style={{ marginBottom: 12 }} />
                        <Typography color="text.secondary" sx={{ maxWidth: 380, mx: 'auto' }}>
                            O PDF é gerado a partir de uma captura completa do dashboard no
                            momento do download — inclui gráficos e diagrama da torre.
                        </Typography>
                    </Box>
                ) : format === 'json' ? (
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 2.5,
                            maxHeight: 380,
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: 12.5,
                            lineHeight: 1.6,
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? alpha('#000', 0.25)
                                    : alpha(theme.palette.text.primary, 0.03),
                            '& .json-key': {
                                color: theme.palette.mode === 'dark' ? '#7dd3fc' : '#0369a1',
                                fontWeight: 600,
                            },
                            '& .json-string': {
                                color: theme.palette.mode === 'dark' ? '#86efac' : '#15803d',
                            },
                            '& .json-number': {
                                color: theme.palette.mode === 'dark' ? '#fca5a5' : '#b91c1c',
                            },
                            '& .json-boolean': {
                                color: theme.palette.mode === 'dark' ? '#fcd34d' : '#a16207',
                                fontWeight: 600,
                            },
                            '& .json-null': { color: 'text.disabled', fontStyle: 'italic' },
                        }}
                        dangerouslySetInnerHTML={{ __html: highlightJson(jsonText) }}
                    />
                ) : (
                    <TableContainer sx={{ maxHeight: 380 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Parâmetro</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">
                                        Valor
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(([k, v]) => (
                                    <TableRow key={k} hover>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                                            {k}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontFamily: 'monospace', fontSize: 12.5 }}
                                        >
                                            {String(v)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            {format !== 'pdf' && (
                <Box sx={{ px: 3, py: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${fileBaseName}.${activeFormat.ext}`}
                        sx={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                    <Chip size="small" variant="outlined" label={formatBytes(activeText)} />
                    {format === 'csv' && (
                        <Chip size="small" variant="outlined" label={`${rows.length} linhas`} />
                    )}
                </Box>
            )}

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Button onClick={onClose}>Cancelar</Button>
                {format !== 'pdf' && (
                    <Button
                        variant="outlined"
                        startIcon={copied ? <Lucide.Check /> : <Lucide.Copy />}
                        onClick={handleCopy}
                        color={copied ? 'success' : 'primary'}
                    >
                        {copied ? 'Copiado!' : 'Copiar'}
                    </Button>
                )}
                <Button
                    variant="contained"
                    startIcon={picker ? <Lucide.Save /> : <Lucide.Download />}
                    onClick={handleDownload}
                    disabled={(format === 'pdf' && pdfGenerating) || saving}
                >
                    {format === 'pdf' && pdfGenerating
                        ? 'Gerando...'
                        : saving
                          ? 'Salvando...'
                          : `${picker ? 'Salvar' : 'Baixar'} ${activeFormat.label}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
